import { apiVersion } from './apis.const';
import { transformResponse } from './apis.util';
import { baseApi } from './base.api';

/**
 * API Urls
 */
const stripePaymentIntentUrl = ({ orgSlug }: { orgSlug: string }) => `/${orgSlug}/${apiVersion}/stripe-payment-intent`;
const stripePaymentUrl = `/${apiVersion}/stripe-payment`;
const paymentMethodsUrl = ({ orgSlug }: { orgSlug: string }) => `/${orgSlug}/${apiVersion}/payment-methods`;
const subscriptionPreviewUrl = ({ orgSlug }: { orgSlug: string }) => `/${orgSlug}/${apiVersion}/subscription-preview`;
const subscriptionPreviewWhenDeletingZoneUrl = ({ orgSlug }: { orgSlug: string }) =>
  `/${orgSlug}/${apiVersion}/subscription-preview/delete-zone`;
const subscriptionUrl = ({ orgSlug }: { orgSlug: string }) => `/${orgSlug}/${apiVersion}/subscription`;

/**
 * Create APIs
 */
export const apiNameApi = baseApi.injectEndpoints({
  overrideExisting: false,

  endpoints: (build) => ({
    fetchPaymentMethod: build.query<FetchPaymentMethodRes, FetchPaymentMethodReq>({
      query: ({ orgSlug }) => ({
        url: paymentMethodsUrl({ orgSlug }),
      }),
      transformResponse,
    }),

    createPaymentMethod: build.mutation<CreatePaymentMethodRes, CreatePaymentMethodReq>({
      query: ({ orgSlug, data }) => ({
        url: paymentMethodsUrl({ orgSlug }),
        method: 'POST',
        body: data,
      }),
      transformResponse,
      invalidatesTags: ['CurrentUser'],
    }),

    stripeIntent: build.mutation<StripeIntentRes, StripeIntentReq>({
      query: ({ orgSlug }) => ({
        url: stripePaymentIntentUrl({ orgSlug }),
        method: 'POST',
      }),
      transformResponse,
    }),

    stripePayment: build.mutation<StripePaymentRes, StripePaymentReq>({
      query: () => ({
        url: stripePaymentUrl,
        method: 'POST',
      }),
      transformResponse,
    }),

    fetchSubscriptionPreview: build.mutation<FetchSubscriptionPreviewRes, FetchSubscriptionPreviewReq>({
      query: ({ orgSlug, products }) => ({
        url: subscriptionPreviewUrl({ orgSlug }),
        method: 'POST',
        body: {
          products,
        },
      }),
      transformResponse,
    }),

    fetchSubscriptionPreviewForDeletingZones: build.mutation<
      FetchSubscriptionPreviewForDeletingZonesRes,
      FetchSubscriptionPreviewForDeletingZonesReq
    >({
      query: ({ orgSlug, zoneIds: zones_to_delete }) => ({
        url: subscriptionPreviewWhenDeletingZoneUrl({ orgSlug }),
        method: 'POST',
        body: {
          zones_to_delete,
        },
      }),
      transformResponse,
    }),

    createSubscription: build.mutation<CreateSubscriptionRes, CreateSubscriptionReq>({
      query: ({ orgSlug, products }) => ({
        url: subscriptionUrl({ orgSlug }),
        method: 'POST',
        body: {
          products,
        },
      }),
      transformResponse,
      invalidatesTags: ['Zones'],
    }),

    updateSubscription: build.mutation<UpdateSubscriptionRes, UpdateSubscriptionReq>({
      query: ({ orgSlug, products }) => ({
        url: subscriptionUrl({ orgSlug }),
        method: 'PUT',
        body: {
          products,
        },
      }),
      transformResponse,
      invalidatesTags: ['Zones'],
    }),
  }),
});

/**
 * Hooks
 */
export const {
  useFetchPaymentMethodQuery,
  useCreatePaymentMethodMutation,
  useStripeIntentMutation,
  useStripePaymentMutation,
  useFetchSubscriptionPreviewMutation,
  useFetchSubscriptionPreviewForDeletingZonesMutation,
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
} = apiNameApi;

/**
 * Types
 */
type FetchPaymentMethodRes = any;
type FetchPaymentMethodReq = {
  orgSlug: string;
};
type CreatePaymentMethodRes = any;
type CreatePaymentMethodReq = {
  orgSlug: string;
  data: any;
};
type StripeIntentRes = any;
type StripeIntentReq = {
  orgSlug: string;
};
type StripePaymentRes = any;
type StripePaymentReq = void;
type FetchSubscriptionPreviewRes = any;
type Product = string;
type ZoneId = number | undefined;
type FetchSubscriptionPreviewReq = {
  orgSlug: string;
  products: {
    [key: Product]: ZoneId[];
  };
};
type FetchSubscriptionPreviewForDeletingZonesRes = any;
type FetchSubscriptionPreviewForDeletingZonesReq = {
  orgSlug: string;
  zoneIds: ZoneId[];
};
type CreateSubscriptionRes = any;
type CreateSubscriptionReq = {
  orgSlug: string;
  products: {
    basic: Array<number>;
    premium: Array<number>;
  };
};
type UpdateSubscriptionRes = any;
type UpdateSubscriptionReq = {
  orgSlug: string;
  products: {
    basic: Array<number>;
    premium: Array<number>;
  };
};
